import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/react';

import { useToastMessages } from '@chargify/toasts';
import {
  Card,
  Button,
  Modal,
  Box,
  SkeletonLoader,
} from '@chargify/custom-ui-components';
import { useChangePlan, useServices } from '../../../../hooks';
import { GlobalStateContext } from '../../../../contexts';
import { subscriptionsSelectors } from '../../../../machines';
import { changePlanSelectors } from '../../../../machines/changePlan';
import { ChangePlanContainer } from '../../../../containers/ChangePlanContainer';
import { PreviewProductMigrationContainer } from '../../../../containers/PreviewProductMigrationContainer';
import { ErrorAlert } from './components/ErrorAlert';

type ChangePlanProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subscription identifier
   */
  subscriptionId: number;
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: (successUpdate?: boolean) => void;
  /**
   * Payment profile process event handler
   */
  onProcessingUpdate: (isProcessing: boolean) => void;
};

const Content = ({
  onClose,
  catalogId,
  subscriptionId,
  displayMode,
  onProcessingUpdate,
}: ChangePlanProps) => {
  const toastService = useToastMessages();
  const { t } = useTranslation(['subscriptions-manager']);
  const { subscriptionsService } = useContext(GlobalStateContext);
  const { modalsService } = useServices();
  const { subscription } = useSelector(
    subscriptionsService,
    subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId)
  );

  const showToast = () => {
    toastService.show({
      level: 'success',
      message: t('plan_change.success_plan_change'),
    });
  };

  const onPlanChangeSuccess = () => {
    onClose(true);
    showToast();
  };

  const onThreeDsConfirmation = (threeDsActionLink: string) => {
    onClose(true);
    modalsService.send({
      type: 'SHOW_THREE_D_SECURE',
      catalogId,
      subscriptionId,
      threeDsActionLink,
      onSuccess: () => {
        showToast();
      },
    });
  };

  const changePlanService = useChangePlan(
    catalogId,
    subscriptionId,
    subscription.product.id,
    onPlanChangeSuccess,
    onThreeDsConfirmation
  );
  const currentPlanId = subscription.product.id;

  const {
    configurePlanStep,
    previewPlanStep,
    configurePlanStepLoading,
    updatePlanLoading,
    previewPlanLoading,
    isError,
  } = useSelector(changePlanService, changePlanSelectors.getChangePlanStatus);

  const selectedPlan = useSelector(
    changePlanService,
    changePlanSelectors.getSelectedPlan
  );
  const error = useSelector(changePlanService, changePlanSelectors.getError);

  useEffect(() => {
    changePlanService.send({ type: 'GET_PRODUCTS' });
  }, []);

  useEffect(() => {
    onProcessingUpdate(updatePlanLoading);
  }, [updatePlanLoading, onProcessingUpdate]);

  const modalTitle = configurePlanStep
    ? t('plan_change.change_plan')
    : t('plan_change.summary_title');

  return (
    <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
      <Card.Header>
        <Modal.Title onClose={onClose} title={modalTitle} />
      </Card.Header>
      <Card.Body>
        {isError ? (
          <ErrorAlert error={error} />
        ) : (
          <>
            {configurePlanStepLoading ? <SkeletonLoader height={400} /> : null}
            {configurePlanStep ? (
              <ChangePlanContainer
                changePlanService={changePlanService}
                currency={subscription.currency}
                currentPlanId={currentPlanId}
              />
            ) : null}
            {previewPlanStep ? (
              <PreviewProductMigrationContainer
                currentProductName={subscription.product.name}
                currency={subscription.currency}
                changePlanService={changePlanService}
              />
            ) : null}
          </>
        )}
      </Card.Body>
      <Card.Footer>
        <Button.Group alignment="right">
          {previewPlanStep && !isError ? (
            <Box marginRight={'auto'}>
              <Button
                color={'secondary'}
                label={t('go_back', { ns: 'common' })}
                onClick={() => changePlanService.send({ type: 'GO_BACK' })}
                iconLeft={'chevronLeft'}
              />
            </Box>
          ) : null}
          <Button
            color="secondary"
            label={t('close', { ns: 'common' })}
            onClick={() => onClose(false)}
          />
          {configurePlanStep && !isError ? (
            <Button
              isLoading={previewPlanLoading}
              label={t('continue', { ns: 'common' })}
              disabled={selectedPlan === currentPlanId}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() =>
                changePlanService.send({
                  type: 'GET_PREVIEW_PRODUCT_MIGRATION',
                })
              }
            />
          ) : null}
          {previewPlanStep && !isError ? (
            <Button
              label={t('confirm_changes', { ns: 'common' })}
              isLoading={updatePlanLoading}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() =>
                changePlanService.send({ type: 'CONFIRM_CHANGE_PLAN' })
              }
            />
          ) : null}
        </Button.Group>
      </Card.Footer>
    </Card>
  );
};

export default Content;
